import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Store, ExternalLink } from 'lucide-react';
import { Link } from 'wouter';

interface NewSpot {
  id: string;
  name: string;
  cuisine?: string;
}

interface NewSpotsResponse {
  restaurants: NewSpot[];
}

export default function NearbyWidget() {
  const { data, isLoading } = useQuery<NewSpotsResponse>({
    queryKey: ['/api/feed/new-spots'],
    queryFn: async (): Promise<NewSpotsResponse> => {
      const res = await fetch('/api/feed/new-spots');
      if (!res.ok) throw new Error('Failed to load new spots');
      return res.json() as Promise<NewSpotsResponse>;
    },
    staleTime: 5 * 60_000,
  });

  const restaurants: NewSpot[] = data?.restaurants ?? [];

  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-1 px-4" style={{ scrollbarWidth: 'none' }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="flex-shrink-0 w-[150px] h-[90px] rounded-lg" />
        ))}
      </div>
    );
  }

  if (restaurants.length === 0) {
    return (
      <div className="px-4 py-3 text-center text-sm text-muted-foreground">
        No new spots added recently — check back soon!
      </div>
    );
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-1 px-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      {restaurants.slice(0, 6).map((r) => (
        <Link key={r.id} to={`/restaurant/${r.id}`}>
          <Card className="flex-shrink-0 w-[150px] hover-elevate active-elevate-2 cursor-pointer">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Store className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-xs truncate leading-tight">{r.name}</div>
                  {r.cuisine && (
                    <div className="text-xs text-muted-foreground truncate">{r.cuisine}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end">
                <Badge variant="secondary" className="text-xs px-1.5">
                  <ExternalLink className="w-3 h-3" />
                </Badge>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}
